"use client";

import { c as _c } from "react/compiler-runtime";
import { AudioLines, Loader2 } from "lucide-react";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { getModelAudioConfig } from "@/lib/audio-gen";
import { useI18n } from "@llmgateway/i18n/react";
import type { AudioFormat } from "@/lib/audio-gen";
interface AudioControlsProps {
  prompt: string;
  setPrompt: (prompt: string) => void;
  selectedModels: string[];
  voice: string;
  setVoice: (value: string) => void;
  audioFormat: AudioFormat;
  setAudioFormat: (value: AudioFormat) => void;
  speed: number;
  setSpeed: (value: number) => void;
  instructions: string;
  setInstructions: (value: string) => void;
  isGenerating: boolean;
  onGenerate: () => void;
}
export function AudioControls(t0) {
  const $ = _c(68);
  const {
    prompt,
    setPrompt,
    selectedModels,
    voice,
    setVoice,
    audioFormat,
    setAudioFormat,
    speed,
    setSpeed,
    instructions,
    setInstructions,
    isGenerating,
    onGenerate
  } = t0;
  const {
    t
  } = useI18n();
  const textareaRef = useRef(null);
  const primaryModel = selectedModels[0] ?? "";
  let canGenerate;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== audioFormat || $[1] !== instructions || $[2] !== isGenerating || $[3] !== onGenerate || $[4] !== primaryModel || $[5] !== prompt || $[6] !== selectedModels.length || $[7] !== setAudioFormat || $[8] !== setInstructions || $[9] !== setPrompt || $[10] !== setSpeed || $[11] !== setVoice || $[12] !== speed || $[13] !== t || $[14] !== voice) {
    const config = getModelAudioConfig(primaryModel);
    let t9;
    if ($[24] !== prompt || $[25] !== selectedModels.length) {
      t9 = prompt.trim().length > 0 && selectedModels.length > 0;
      $[24] = prompt;
      $[25] = selectedModels.length;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    canGenerate = t9;
    let t10;
    if ($[27] !== canGenerate || $[28] !== isGenerating || $[29] !== onGenerate || $[30] !== prompt) {
      t10 = e => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          if (prompt.trim() && !isGenerating && canGenerate) {
            onGenerate();
          }
        }
      };
      $[27] = canGenerate;
      $[28] = isGenerating;
      $[29] = onGenerate;
      $[30] = prompt;
      $[31] = t10;
    } else {
      t10 = $[31];
    }
    const handleKeyDown = t10;
    t8 = "border-b bg-background p-4";
    t5 = "max-w-4xl mx-auto space-y-3";
    let t11;
    if ($[32] !== setPrompt) {
      t11 = e_0 => setPrompt(e_0.target.value);
      $[32] = setPrompt;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    let t12;
    if ($[34] !== t) {
      t12 = t("studio.textToSpeechPrompt");
      $[34] = t;
      $[35] = t12;
    } else {
      t12 = $[35];
    }
    if ($[36] !== handleKeyDown || $[37] !== isGenerating || $[38] !== prompt || $[39] !== t11 || $[40] !== t12) {
      t6 = <div className="rounded-md border-input border dark:bg-input/30 shadow-xs focus-within:ring-1 focus-within:ring-ring transition-colors"><Textarea ref={textareaRef} value={prompt} onChange={t11} onKeyDown={handleKeyDown} placeholder={t12} className="min-h-[80px] max-h-[200px] resize-none border-0 bg-transparent dark:bg-transparent focus-visible:ring-0 shadow-none" disabled={isGenerating} /></div>;
      $[36] = handleKeyDown;
      $[37] = isGenerating;
      $[38] = prompt;
      $[39] = t11;
      $[40] = t12;
      $[41] = t6;
    } else {
      t6 = $[41];
    }
    t7 = config.supportsInstructions && <Input value={instructions} onChange={e_1 => setInstructions(e_1.target.value)} placeholder={t("studio.textToSpeechStyle")} disabled={isGenerating} />;
    t1 = "flex flex-wrap items-center gap-2";
    let t13;
    if ($[42] !== t) {
      t13 = t("studio.voice");
      $[42] = t;
      $[43] = t13;
    } else {
      t13 = $[43];
    }
    let t14;
    if ($[44] !== t13) {
      t14 = <SelectTrigger size="sm" className="min-w-[130px]"><SelectValue placeholder={t13} /></SelectTrigger>;
      $[44] = t13;
      $[45] = t14;
    } else {
      t14 = $[45];
    }
    t2 = <Select value={voice} onValueChange={setVoice}>{t14}<SelectContent>{config.voices.map(_temp)}</SelectContent></Select>;
    t3 = config.availableFormats.length > 1 && <Select value={audioFormat} onValueChange={val => setAudioFormat(val as AudioFormat)}><SelectTrigger size="sm" className="min-w-[90px]"><SelectValue placeholder={t("studio.format")} /></SelectTrigger><SelectContent>{config.availableFormats.map(_temp2)}</SelectContent></Select>;
    t4 = config.supportsSpeed && <Select value={String(speed)} onValueChange={val_0 => setSpeed(Number(val_0))}><SelectTrigger size="sm" className="min-w-[90px]"><SelectValue placeholder={t("studio.speed")} /></SelectTrigger><SelectContent>{config.availableSpeeds.map(_temp3)}</SelectContent></Select>;
    $[0] = audioFormat;
    $[1] = instructions;
    $[2] = isGenerating;
    $[3] = onGenerate;
    $[4] = primaryModel;
    $[5] = prompt;
    $[6] = selectedModels.length;
    $[7] = setAudioFormat;
    $[8] = setInstructions;
    $[9] = setPrompt;
    $[10] = setSpeed;
    $[11] = setVoice;
    $[12] = speed;
    $[13] = t;
    $[14] = voice;
    $[15] = canGenerate;
    $[16] = t1;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    canGenerate = $[15];
    t1 = $[16];
    t2 = $[17];
    t3 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
    t8 = $[23];
  }
  let t9;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="flex-1" />;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  const t10 = isGenerating || !canGenerate;
  let t11;
  if ($[47] !== isGenerating || $[48] !== t) {
    t11 = isGenerating ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t("common.generating")}</> : <><AudioLines className="h-4 w-4 mr-2" />{t("common.generate")}</>;
    $[47] = isGenerating;
    $[48] = t;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  let t12;
  if ($[50] !== onGenerate || $[51] !== t10 || $[52] !== t11) {
    t12 = <Button onClick={onGenerate} disabled={t10} className="min-w-[120px]">{t11}</Button>;
    $[50] = onGenerate;
    $[51] = t10;
    $[52] = t11;
    $[53] = t12;
  } else {
    t12 = $[53];
  }
  let t13;
  if ($[54] !== t1 || $[55] !== t12 || $[56] !== t2 || $[57] !== t3 || $[58] !== t4) {
    t13 = <div className={t1}>{t2}{t3}{t4}{t9}{t12}</div>;
    $[54] = t1;
    $[55] = t12;
    $[56] = t2;
    $[57] = t3;
    $[58] = t4;
    $[59] = t13;
  } else {
    t13 = $[59];
  }
  let t14;
  if ($[60] !== t13 || $[61] !== t5 || $[62] !== t6 || $[63] !== t7) {
    t14 = <div className={t5}>{t6}{t7}{t13}</div>;
    $[60] = t13;
    $[61] = t5;
    $[62] = t6;
    $[63] = t7;
    $[64] = t14;
  } else {
    t14 = $[64];
  }
  let t15;
  if ($[65] !== t14 || $[66] !== t8) {
    t15 = <div className={t8}>{t14}</div>;
    $[65] = t14;
    $[66] = t8;
    $[67] = t15;
  } else {
    t15 = $[67];
  }
  return t15;
}
function _temp3(s) {
  return <SelectItem key={s} value={String(s)}>{s}x</SelectItem>;
}
function _temp2(format) {
  return <SelectItem key={format} value={format}>{format.toUpperCase()}</SelectItem>;
}
function _temp(v) {
  return <SelectItem key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</SelectItem>;
}